import {create} from 'zustand';
import {Role, CreateRoleRequest, RoleFilters} from '../models/role.model';
import {RoleRepository} from '../repository/RoleRepository';
import {RoleRepositoryImpl} from '../repository/RoleRepositoryImpl';

interface RoleState {
  // Data
  roles: Role[];
  currentRole: Role | null;
  pagination: any;

  // Loading states
  loading: boolean;
  creating: boolean;
  deleting: boolean;

  // Error states
  error: string | null;
  createError: string | null;
  deleteError: string | null;

  // Filters
  filters: RoleFilters;

  // Actions
  fetchRoles: (filters?: RoleFilters) => Promise<void>;
  getRoleById: (id: number) => Promise<Role | null>;
  createRole: (roleData: CreateRoleRequest) => Promise<Role>;
  deleteRole: (id: number) => Promise<void>;

  // Utility actions
  setFilters: (filters: Partial<RoleFilters>) => void;
  clearError: () => void;
  clearCreateError: () => void;
  clearDeleteError: () => void;
  reset: () => void;
}

export const useRoleStore = create<RoleState>((set, get) => {
  const roleRepository = new RoleRepositoryImpl();

  return {
    // Initial state
    roles: [],
    currentRole: null,
    pagination: null,

    // Loading states
    loading: false,
    creating: false,
    deleting: false,

    // Error states
    error: null,
    createError: null,
    deleteError: null,

    // Filters
    filters: {},

    // Actions
    fetchRoles: async (filters?: RoleFilters) => {
      set({loading: true, error: null});
      try {
        const result = await roleRepository.getRoles(filters);
        set({
          roles: result.roles || [],
          pagination: result.pagination,
          loading: false
        });
      } catch (error) {
        // Error is handled by StandardizedErrorHandler at component level
        // Provide fallback empty array to prevent undefined errors
        set({
          roles: [],
          error: error instanceof Error ? error.message : 'Failed to fetch roles',
          loading: false
        });
        throw error; // Re-throw to allow component-level error handling
      }
    },

    getRoleById: async (id: number) => {
      try {
        const role = await roleRepository.getRoleById(id);
        set({currentRole: role});
        return role;
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Failed to fetch role',
          currentRole: null
        });
        return null;
      }
    },

    createRole: async (roleData: CreateRoleRequest) => {
      set({creating: true, createError: null});
      try {
        const newRole = await roleRepository.createRole(roleData);

        // Since backend returns null data, refresh the roles list to get the actual data
        const result = await roleRepository.getRoles();
        set({
          roles: result.roles || [],
          pagination: result.pagination,
          creating: false
        });

        return newRole;
      } catch (error) {
        set({
          createError: error instanceof Error ? error.message : 'Failed to create role',
          creating: false
        });
        throw error;
      }
    },


    deleteRole: async (id: number) => {
      set({deleting: true, deleteError: null});
      try {
        await roleRepository.deleteRole(id);
        set(state => ({
          roles: state.roles.filter(role => role.id !== id),
          deleting: false
        }));
      } catch (error) {
        set({
          deleteError: error instanceof Error ? error.message : 'Failed to delete role',
          deleting: false
        });
        throw error;
      }
    },

    // Utility actions
    setFilters: (filters: Partial<RoleFilters>) => {
      set(state => ({
        filters: {...state.filters, ...filters}
      }));
    },

    clearError: () => set({error: null}),
        clearCreateError: () => set({createError: null}),
        clearDeleteError: () => set({deleteError: null}),

    reset: () => set({
      roles: [],
      currentRole: null,
      pagination: null,
      loading: false,
      creating: false,
      updating: false,
      deleting: false,
      error: null,
      createError: null,
      updateError: null,
      deleteError: null,
      filters: {}
    })
  };
});
